import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

function sanitizePhone(phone) {
    // Keep + and digits only for tel: link.
    return String(phone || '').replace(/[^\d+]/g, '');
}

function maskPhone(phone, hiddenEndDigits = 4) {
    // Shows the beginning of the phone, masks the LAST N digits.
    const src = String(phone || '');
    const digits = src.replace(/\D/g, '');
    if (!digits) return src;
    if (digits.length <= hiddenEndDigits) return src;

    const totalDigits = digits.length;
    const startMaskAt = totalDigits - hiddenEndDigits; // 0-based index where masking begins
    let digitsSeen = 0;

    return src
        .split('')
        .map((ch) => {
            if (!/\d/.test(ch)) return ch;
            const shouldMask = digitsSeen >= startMaskAt;
            digitsSeen += 1;
            return shouldMask ? '•' : ch;
        })
        .join('');
}

async function copyToClipboard(text) {
    const t = String(text || '');
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(t);
            return true;
        }
    } catch (_) {
        // fallback below
    }
    try {
        const ta = document.createElement('textarea');
        ta.value = t;
        ta.style.position = 'fixed';
        ta.style.left = '-1000px';
        ta.style.top = '-1000px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return !!ok;
    } catch (_) {
        return false;
    }
}

const ContactsCard = ({ profile, locationLine, links, showWebsite, onUpgradeClick }) => {
    const [phoneRevealed, setPhoneRevealed] = useState(false);

    const phoneRaw = profile?.phone ? String(profile.phone) : '';
    const phoneMasked = useMemo(() => maskPhone(phoneRaw, 4), [phoneRaw]);
    const phoneSanitized = useMemo(() => sanitizePhone(phoneRaw), [phoneRaw]);
    const phoneLabel = phoneRevealed ? phoneRaw : phoneMasked;
    const phoneHint = phoneRevealed ? 'Нажмите ещё раз, чтобы позвонить' : 'Нажмите, чтобы показать номер';

    const onPhoneClick = () => {
        if (!phoneRaw) return;
        if (!phoneRevealed) {
            setPhoneRevealed(true);
            return;
        }
        // Second click: start call
        if (!phoneSanitized) return;
        window.location.href = `tel:${phoneSanitized}`;
    };

    return (
        <div className="rounded-2xl bg-white border border-secondary-200 shadow-soft p-5">
            <div className="text-sm font-semibold text-secondary-900 mb-3">Контакты</div>

            {profile.display_name ? (
                <div className="text-sm text-secondary-700">
                    <div className="text-xs text-secondary-500 mb-1">Имя</div>
                    <div className="text-secondary-900 font-medium break-words">{profile.display_name}</div>
                </div>
            ) : null}

            {locationLine ? (
                <div className={`text-sm text-secondary-700 ${profile.display_name ? 'mt-4' : ''}`}>
                    <div className="text-xs text-secondary-500 mb-1">Страна, город</div>
                    <div className="text-secondary-900 font-medium break-words">{locationLine}</div>
                </div>
            ) : null}

            {profile.website && showWebsite ? (
                <div className={`text-sm text-secondary-700 ${(profile.display_name || locationLine) ? 'mt-4' : ''}`}>
                    <div className="text-xs text-secondary-500 mb-1">Сайт</div>
                    <a
                        href={decodeURIComponent(profile.website)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-700 hover:underline break-words"
                    >
                        {decodeURIComponent(profile.website)}
                    </a>
                </div>
            ) : null}

            {/* {profile.website && !showWebsite ? (
            <div className={`text-sm text-secondary-700 ${(profile.display_name || locationLine) ? 'mt-4' : ''}`}>
                <button type="button" className="text-primary-700 hover:underline" onClick={onUpgradeClick}>
                    Показать сайт (нужно «Полный доступ»)
                </button>
            </div>
        ) : null} */}

            {profile.phone ? (
                <div className={`text-sm text-secondary-700 ${(profile.website || profile.display_name || locationLine) ? 'mt-4' : ''}`}>
                    <div className="text-xs text-secondary-500 mb-1">Телефон</div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="text-primary-700 hover:underline"
                            onClick={onPhoneClick}
                            aria-label={phoneRevealed ? 'Позвонить по номеру телефона' : 'Показать номер телефона'}
                            title={phoneHint}
                        >
                            {phoneLabel}
                        </button>

                        {!phoneRevealed ? null : (
                            <button
                                type="button"
                                onClick={async () => {
                                    const ok = await copyToClipboard(phoneRaw);
                                    if (ok) toast.success('Скопировано');
                                    else toast.error('Не удалось скопировать');
                                }}
                                className="px-2 py-1 rounded-lg bg-primary-500 text-white hover:bg-primary-600 text-xs"
                                aria-label="Скопировать номер телефона"
                            >
                                Копировать
                            </button>
                        )}
                    </div>
                    <div className="mt-1 text-xs text-secondary-500">{phoneHint}</div>
                </div>
            ) : null}

            {links.length > 0 && (
                <div className="mt-4">
                    <div className="text-xs text-secondary-500 mb-2">Социальные сети</div>
                    <div className="flex flex-wrap gap-2">
                        {links.map((l) => (
                            <a
                                key={l.label}
                                href={l.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1.5 rounded-full bg-primary-500 text-sm text-white hover:bg-primary-700"
                            >
                                {l.label}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactsCard;
